/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Box from "components/generals/Box";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { AppointmentInterface, HeaderEnum, ProgressNoteInterface } from "@malmur-technologies/medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { H5 } from "components/generals/_Titles";
import { useTranslation } from "react-i18next";
import { calculateAge } from "utils/generic.utils";
import { PrintPrescriptions } from "../templates/PrintPrescriptionsTemplate";
import PermissionWrapper from "components/generals/PermissionWrapper";
import ButtonDialog from "components/generals/ButtonDialog";
import { EntityPermissions, PermissionsUser } from "types";
import Typography from "@mui/material/Typography";
import { useAuthenticatedAsset } from "hooks/useAuthenticatedAsset.hook";
import { AssetInterface } from "@malmur-technologies/medicalsystem-shared";

/**
 * Properties for the PrescriptionTemplate component.
 * 
 * @interface PrescriptionTemplateProps
 * @property {string} [appointmentId] - Appointment ID to load data for
 * @property {boolean} [printbtn] - Whether to show print button
 * @property {string} [medicationText] - Medication prescription HTML content
 * @property {string} [labText] - Lab test orders HTML content
 * @property {string} [imagingText] - Imaging test orders HTML content
 * @property {string} [notesMedicineText] - Additional notes for medication
 * @property {string} [notesLabText] - Additional notes for lab tests
 * @property {string} [notesImagingText] - Additional notes for imaging
 */
interface PrescriptionTemplateProps {
  appointmentId?: string;
  printbtn?: boolean;
  medicationText?: string;
  labText?: string;
  imagingText?: string;
  notesMedicineText?: string;
  notesLabText?: string;
  notesImagingText?: string;
}

/**
 * Prescription template component for viewing and printing prescriptions.
 * Displays medication, lab, and imaging orders with print functionality.
 * 
 * Features:
 * - Loads appointment and progress note data
 * - Displays three prescription types: medication, lab tests, imaging
 * - Accordion layout for each prescription type
 * - Print functionality with selective section printing
 * - Opens print dialog in centered popup window
 * - Uses PrintPrescriptions template for formatting
 * - Calculated patient age from DOB
 * - Permission-based print button
 * - i18n translation support
 * 
 * Prescription Sections:
 * - Medication: Prescription orders and instructions
 * - Lab Test: Laboratory test orders
 * - Imaging Test: Radiology/imaging orders
 * 
 * Print Behavior:
 * - Opens 800x600 centered popup window
 * - Includes formatted prescription template HTML
 * - Contains original and copy versions
 * - Auto-triggers print dialog
 * - Closes window after printing
 * 
 * Notes:
 * - Each section can have additional notes
 * - Sections only shown if content provided
 * - Accordion list built dynamically from available sections
 * 
 * @param {PrescriptionTemplateProps} props - Component properties
 * @returns {JSX.Element} Prescription template card with print capability
 */
const PrescriptionTemplate: FC<PrescriptionTemplateProps> = ({
  appointmentId,
  printbtn,
  medicationText,
  labText,
  imagingText,
  notesMedicineText,
  notesLabText,
  notesImagingText,
}) => {
  const { t } = useTranslation();
  const [appointmentModel, setAppointmentModel] = useState<Partial<AppointmentInterface>>();
  const [progressNoteModel, setProgressNoteModel] = useState<Partial<ProgressNoteInterface>>();
  const [providerAsset, setProviderAsset] = useState<Partial<AssetInterface> | undefined>(undefined);
  const [_isLoading, _error, sendRequest] = useHttp();
  const [, , sendRequestAsset] = useHttp();
  const printableContentRef = useRef<HTMLDivElement | null>(null);
  
  // Use the authenticated asset hook to get the blob URL for the provider's image
  const { blobUrl: previewImageUrl } = useAuthenticatedAsset(providerAsset);
  
  const age = useMemo(() => {
    return calculateAge(appointmentModel?.patient?.person?.dob);
  }, [appointmentModel?.patient?.person?.dob]);
  useEffect(() => {
    if (appointmentId) {
      sendRequest(
        CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
        onGetAppointment,
        "GET",
        undefined,
        [
          {
            header: HeaderEnum.RESOURCE_ID,
            value: appointmentId,
          },
        ]
      );
    }
  }, [appointmentId]);

  useEffect(() => {
    if (appointmentModel?.provider?.id) {
      sendRequestAsset(
        CoreAPI_URLs.PROVIDER_ASSET_RELATION,
        (relations: Array<{ asset: AssetInterface }>) => {
          if (relations && relations.length > 0 && relations[0].asset) {
            setProviderAsset(relations[0].asset);
          }
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.RESOURCE_ID, value: appointmentModel.provider.id }]
      );
    }
  }, [appointmentModel?.provider?.id]);
  const accordionsList = [
    medicationText && { id: "medication", name: t("progressNoteForm.medication") },
    labText && { id: "labtest", name: t("progressNoteForm.labTest") },
    imagingText && { id: "imagingtest", name: t("progressNoteForm.imagingTest") },
  ].filter(Boolean);
  const onGetAppointment = (appointment: AppointmentInterface) => {
    if (appointment) {
      setAppointmentModel(appointment);
    } else {
      setAppointmentModel(null);
    }
    if (appointment.progressNote) {
      setProgressNoteModel(appointment.progressNote);
    } else setProgressNoteModel(null);
  };

  // Convert blob URL to base64 data URL for printing
  const blobUrlToBase64 = async (blobUrl: string): Promise<string> => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handlePrint = async (sectionsToPrint: string[]) => {
    const printableContent = printableContentRef.current;
    if (printableContent) {
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const printWindowWidth = 800;
      const printWindowHeight = 600;

      const left = screenWidth / 2 - printWindowWidth / 2;
      const top = screenHeight / 2 - printWindowHeight / 2;

      // Convert blob URL to base64 for embedding in print window
      let imageBase64: string | null = null;
      if (previewImageUrl) {
        try {
          imageBase64 = await blobUrlToBase64(previewImageUrl);
        } catch (error) {
          console.error("Failed to convert image to base64:", error);
        }
      }

      const htmlContent = PrintPrescriptions({
        appointmentModel,
        progressNoteModel,
        age,
        medicationTest: medicationText,
        t,
        printLabTest: sectionsToPrint.includes("labtest"),
        printMedication: sectionsToPrint.includes("medication"),
        printImagingTest: sectionsToPrint.includes("imagingtest"),
        labTest: labText,
        imagingTest: imagingText,
        notesMedicine: notesMedicineText,
        notesLab: notesLabText,
        notesImaging: notesImagingText,
        previewImageUrl: imageBase64,
      });
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(
        url,
        "",
        `width=${printWindowWidth},height=${printWindowHeight},left=${left},top=${top}`
      );

      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          URL.revokeObjectURL(url);
        };
      }
    }
  };

  return (
    <Card
      id="progressNoteCard"
      sx={{
        position: "sticky",
        top: 0,
        width: "500px",
      }}
      ref={printableContentRef}
    >
      <Box
        borderRadius="lg"
        ml={"1rem"}
        mt={2}
        width="calc(100% - 2rem)"
        height="calc(100% - 2rem)"
        paddingBottom={"1rem"}
      >
        {printbtn && (
          <Box mr={2} mt={2} textAlign={"right"}>
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.PROGRESS_NOTE].PRINT]}
            >
              <ButtonDialog
                color={true}
                startIcon={true}
                disabled={false}
                onAcceptHandle={(e) => {
                  handlePrint(e);
                }}
                buttonText={t("progressNoteForm.btnDialogPrint")}
                title={t("progressNoteForm.btnDialogTitle")}
                description={accordionsList}
                labelledbyID={t("progressNoteForm.btnDialogPrint")}
                describedbyID={"PrintDescription"}
              />
            </PermissionWrapper>
          </Box>
        )}
        {!medicationText && !labText && !imagingText ? (
          <Typography variant="h5">{t("progressNoteForm.noPrescriptionInformation")}</Typography>
        ) : (
          <>
            {medicationText && (
              <Grid id="hpi" container spacing={1} paddingTop={2}>
                <Grid item xs={12}>
                  <H5>{t("progressNoteForm.medication")}:</H5>
                </Grid>
                <Grid item xs={12}>
                  <div className="caption" dangerouslySetInnerHTML={{ __html: medicationText }} />
                </Grid>
              </Grid>
            )}
            {labText && (
              <Grid id="hpi" container spacing={1} paddingTop={2}>
                <Grid item xs={12}>
                  <H5>{t("progressNoteForm.labTest")}:</H5>
                </Grid>
                <Grid item xs={12}>
                  <div className="caption" dangerouslySetInnerHTML={{ __html: labText }} />
                </Grid>
              </Grid>
            )}
            {imagingText && (
              <Grid id="hpi" container spacing={1} paddingTop={2}>
                <Grid item xs={12}>
                  <H5>{t("progressNoteForm.imagingTest")}:</H5>
                </Grid>
                <Grid item xs={12}>
                  <div className="caption" dangerouslySetInnerHTML={{ __html: imagingText }} />
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Box>
    </Card>
  );
};

export default PrescriptionTemplate;
